import React, { memo } from "react";
import { Text, View } from "@react-pdf/renderer";
import { Style } from "@react-pdf/types";
interface MarkdownRendererProps {
  content: string;
  customStyle?: {
    container?: Style | Style[];
    h1?: {
      container?: Style | Style[];
      text?: Style | Style[];
    };
    h2?: {
      container?: Style | Style[];
      text?: Style | Style[];
    };
    h3?: {
      container?: Style | Style[];
      text?: Style | Style[];
    };
    text?: {
      container?: Style | Style[];
      text?: Style | Style[];
    };
    listItem?: {
      container?: Style | Style[];
      text?: Style | Style[];
    };
    blockquote?: {
      container?: Style | Style[];
      text?: Style | Style[];
    };
    hr?: Style | Style[];
  };
}

interface ParsedContent {
  type: string;
  content: { text: string; bold: boolean; italic: false }[];
}

const parseInlineStyles = (text: string) => {
  const parts = [];
  let currentPart = { text: "", bold: false, italic: false };
  let isBold = false;
  let isItalic = false;

  for (let i = 0; i < text.length; i++) {
    if (text[i] === "*" || text[i] === "_") {
      if (i + 1 < text.length && text[i + 1] === text[i]) {
        isBold = !isBold;
        i++; // Skip next asterisk/underscore
      } else {
        isItalic = !isItalic;
      }

      if (currentPart.text) {
        parts.push(currentPart);
      }
      currentPart = { text: "", bold: isBold, italic: isItalic };
    } else {
      currentPart.text += text[i];
    }
  }

  if (currentPart.text) {
    parts.push(currentPart);
  }

  return parts;
};

const parseMarkdown = (text: string) => {
  const lines = text.split("\n");
  return lines.map((line) => {
    if (line.startsWith("# "))
      return { type: "h1", content: parseInlineStyles(line.slice(2)) };
    if (line.startsWith("## "))
      return { type: "h2", content: parseInlineStyles(line.slice(3)) };
    if (line.startsWith("### "))
      return { type: "h3", content: parseInlineStyles(line.slice(4)) };
    if (line.startsWith("- ") || line.startsWith("* ") || line.startsWith("+ "))
      return { type: "listItem", content: parseInlineStyles(line.slice(2)) };
    if (line.match(/^\d+\. /))
      return {
        type: "orderedListItem",
        content: parseInlineStyles(line.slice(line.indexOf(" ") + 1)),
      };
    if (line.startsWith("> "))
      return { type: "blockquote", content: parseInlineStyles(line.slice(2)) };
    if (line === "---") return { type: "hr", content: [] };
    return { type: "paragraph", content: parseInlineStyles(line) };
  });
};

const InlineContent = memo(
  ({
    content,
    style = {},
  }: {
    content: { text: string; bold: boolean; italic: boolean }[];
    style?: Style | Style[];
  }) => {
    return (
      <Text>
        {content.map((part, index) => (
          <Text
            key={index}
            style={{
              ...style,
              //   fontWeight: part.bold ? "bold" : "normal",
              fontWeight: part.bold ? "bold" : "normal",
              //   fontStyle: part.italic ? "italic" : "normal",
              fontStyle: part.italic ? "normal" : "normal",
            }}
          >
            {part.text}
          </Text>
        ))}
      </Text>
    );
  },
);

const renderContent = (
  parsedContent: ParsedContent[],
  customStyle: MarkdownRendererProps["customStyle"] = {},
) => {
  return parsedContent.map((item, index) => {
    switch (item.type) {
      case "h1":
        return (
          <View
            key={index}
            style={{ marginBottom: 10, ...(customStyle?.h1?.container || {}) }}
          >
            <InlineContent
              content={item.content}
              style={{
                fontSize: 24,
                fontWeight: "bold",
                ...(customStyle?.h1?.text || {}),
              }}
            />
          </View>
        );
      case "h2":
        return (
          <View
            key={index}
            style={{ marginBottom: 8, ...(customStyle?.h2?.container || {}) }}
          >
            <InlineContent
              content={item.content}
              style={{
                fontSize: 20,
                fontWeight: "bold",
                ...(customStyle?.h2?.text || {}),
              }}
            />
          </View>
        );
      case "h3":
        return (
          <View
            key={index}
            style={{ marginBottom: 6, ...(customStyle?.h3?.container || {}) }}
          >
            <InlineContent
              content={item.content}
              style={{
                fontSize: 16,
                fontWeight: "bold",
                ...(customStyle?.h3?.text || {}),
              }}
            />
          </View>
        );
      case "listItem":
        return (
          <View
            key={index}
            style={{
              marginLeft: 16,
              marginBottom: 4,
              ...(customStyle?.listItem?.container || {}),
            }}
          >
            <InlineContent
              content={[
                { text: "• ", bold: true, italic: false },
                ...item.content,
              ]}
              style={{
                fontSize: 12,
                ...(customStyle?.listItem?.text || {}),
              }}
            />
          </View>
        );
      case "orderedListItem":
        return (
          <View
            key={index}
            style={{
              marginLeft: 16,
              marginBottom: 4,
              ...(customStyle?.listItem?.container || {}),
            }}
          >
            <InlineContent
              content={item.content}
              style={{
                fontSize: 12,
                ...(customStyle?.listItem?.text || {}),
              }}
            />
          </View>
        );
      case "blockquote":
        return (
          <View
            key={index}
            style={{
              borderLeftWidth: 2,
              borderColor: "#666",
              paddingLeft: 8,
              marginLeft: 8,
              marginVertical: 8,
              ...(customStyle?.blockquote?.container || {}),
            }}
          >
            <InlineContent
              content={item.content}
              style={{
                fontSize: 12,
                ...(customStyle?.blockquote?.text || {}),
              }}
            />
          </View>
        );
      case "hr":
        return (
          <View
            key={index}
            style={{
              borderTopWidth: 1,
              borderColor: "#666",
              marginVertical: 16,
              ...(customStyle?.hr || {}),
            }}
          />
        );
      default:
        return (
          <View
            key={index}
            style={{ marginBottom: 4, ...(customStyle?.text?.container || {}) }}
          >
            <InlineContent
              content={item.content}
              style={{
                fontSize: 12,
                ...(customStyle?.text?.text || {}),
              }}
            />
          </View>
        );
    }
  });
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  customStyle,
}) => {
  const parsedContent = parseMarkdown(content);

  return (
    <View style={{ ...(customStyle?.container || {}) }}>
      {renderContent(parsedContent as ParsedContent[], customStyle)}
    </View>
  );
};

export default memo(
  MarkdownRenderer,
  (prevProps, nextProps) =>
    prevProps.content === nextProps.content &&
    JSON.stringify(prevProps.customStyle) ===
      JSON.stringify(nextProps.customStyle),
);
